import { useCallback, useMemo } from "react";
import {
  canPaginateList,
  canSelectionExportList,
  effectiveVipLevel,
  resolvePlanCapabilities,
  type DocumentListKind,
  type PlanCapabilityFlags,
} from "@/domain/billing/planCapabilities";
import type { BillingGateReason, BillingStatus } from "@/types/api";
import { useBillingStatus } from "@/features/billing/hooks/useBillingStatus";

export type GatedCapability =
  | "inline_pdf"
  | "paginate"
  | "selection_export"
  | "bulk_export"
  | "pay_links_csv"
  | "redownload_history";

const CAPABILITY_KEY: Record<
  Exclude<GatedCapability, "paginate" | "selection_export">,
  keyof PlanCapabilityFlags
> = {
  inline_pdf: "canInlinePdf",
  bulk_export: "canBulkExport",
  pay_links_csv: "canPayLinksCsv",
  redownload_history: "canRedownloadHistory",
};

export function gateReasonFor(
  status: BillingStatus | undefined,
  capability: GatedCapability,
  listKind?: DocumentListKind,
): BillingGateReason | null {
  if (!status) return null;
  const vip = effectiveVipLevel(status);
  const allowed = isCapabilityAllowed(status, capability, listKind, vip);
  if (allowed) return null;
  if (capability === "bulk_export") {
    return "upgrade_to_pro_required";
  }
  return "subscription_required";
}

function isCapabilityAllowed(
  status: BillingStatus,
  capability: GatedCapability,
  listKind: DocumentListKind | undefined,
  vip: string,
): boolean {
  if (capability === "paginate") {
    if (!listKind) return resolvePlanCapabilities(status).canPaginate;
    return canPaginateList(vip, listKind);
  }
  if (capability === "selection_export") {
    if (!listKind) return resolvePlanCapabilities(status).canSelectionExport;
    return canSelectionExportList(vip, listKind);
  }
  const caps = resolvePlanCapabilities(status);
  return caps[CAPABILITY_KEY[capability]];
}

export function usePlanAccess() {
  const { status, mock, ...query } = useBillingStatus();

  const caps = useMemo(() => resolvePlanCapabilities(status), [status]);
  const vipLevel = useMemo(
    () => (status ? effectiveVipLevel(status) : "starter"),
    [status],
  );

  const gateReason = useCallback(
    (capability: GatedCapability, listKind?: DocumentListKind) =>
      gateReasonFor(status, capability, listKind),
    [status],
  );

  const withGate = useCallback(
    (
      capability: GatedCapability,
      onAllowed: () => void,
      listKind?: DocumentListKind,
    ): BillingGateReason | null => {
      const reason = gateReasonFor(status, capability, listKind);
      if (reason) return reason;
      onAllowed();
      return null;
    },
    [status],
  );

  return {
    status,
    mock,
    ...query,
    ...caps,
    vipLevel,
    gateReason,
    withGate,
  };
}

export type { DocumentListKind };
